export interface CookieOptions {
  path?: string;
  domain?: string;
  expires?: Date | number;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  maxAge?: number;
}

// Cookie configuration
const COOKIE_CONFIG = {
  name: 'itria-token',
  options: {
    path: '/',
    secure: import.meta.env.PROD, // HTTPS only in production
    sameSite: 'strict' as const,
    maxAge: 7 * 24 * 60 * 60, // 7 days
    // Note: httpOnly cannot be set from JavaScript (requires server-side)
  },
};

/**
 * Set authentication token in secure cookie
 */
export const setTokenCookie = (
  token: string,
  options?: Partial<CookieOptions>
): void => {
  try {
    const finalOptions = { ...COOKIE_CONFIG.options, ...options };
    const cookieString = [
      `${COOKIE_CONFIG.name}=${encodeURIComponent(token)}`,
      finalOptions.path && `path=${finalOptions.path}`,
      finalOptions.domain && `domain=${finalOptions.domain}`,
      finalOptions.expires instanceof Date
        ? `expires=${finalOptions.expires.toUTCString()}`
        : finalOptions.expires
          ? `expires=${new Date(Date.now() + finalOptions.expires * 1000).toUTCString()}`
          : finalOptions.maxAge
            ? `max-age=${finalOptions.maxAge}`
            : '',
      finalOptions.secure && 'secure',
      finalOptions.sameSite && `samesite=${finalOptions.sameSite}`,
    ]
      .filter(Boolean)
      .join('; ');

    document.cookie = cookieString;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to set token cookie:', error);
    // Fallback to localStorage if cookies fail
    localStorage.setItem('itria-token-fallback', token);
  }
};

/**
 * Get authentication token from cookie
 */
export const getTokenCookie = (): string | null => {
  try {
    const cookies = document.cookie.split(';').reduce(
      (acc, cookie) => {
        const [name, value] = cookie.trim().split('=');
        acc[name] = decodeURIComponent(value || '');
        return acc;
      },
      {} as Record<string, string>
    );

    return cookies[COOKIE_CONFIG.name] || null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to get token cookie:', error);
    return null;
  }
};

/**
 * Remove authentication token cookie
 */
export const removeTokenCookie = (): void => {
  try {
    // Match SameSite/Secure used when setting the cookie; otherwise some
    // browsers keep the old cookie and logout cannot clear the session.
    const parts = [
      `${COOKIE_CONFIG.name}=`,
      `path=${COOKIE_CONFIG.options.path}`,
      'expires=Thu, 01 Jan 1970 00:00:01 GMT',
      `samesite=${COOKIE_CONFIG.options.sameSite}`,
    ];
    if (import.meta.env.PROD) {
      parts.push('secure');
    }
    document.cookie = parts.join('; ');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to remove token cookie:', error);
  }
};

/**
 * Check if token cookie exists
 */
export const hasTokenCookie = (): boolean => {
  return !!getTokenCookie();
};

/**
 * Migrate token from localStorage to cookie
 */
export const migrateFromLocalStorage = (): {
  migrated: boolean;
  tokenFound: boolean;
} => {
  const localStorageKey = 'itria-token';
  const existingToken = localStorage.getItem(localStorageKey);

  if (existingToken) {
    setTokenCookie(existingToken);
    localStorage.removeItem(localStorageKey);
    return { migrated: true, tokenFound: true };
  }

  return { migrated: false, tokenFound: false };
};

/**
 * Validate JWT token format
 */
export const isValidTokenFormat = (token: string): boolean => {
  try {
    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
  } catch {
    return false;
  }
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  try {
    const cookies = document.cookie.split(';').reduce(
      (acc, cookie) => {
        const [cookieName, value] = cookie.trim().split('=');
        acc[cookieName] = decodeURIComponent(value || '');
        return acc;
      },
      {} as Record<string, string>
    );

    return cookies[name] || null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to get cookie ${name}:`, error);
    return null;
  }
};
